import { Box, styled } from '@mui/material';
import React from 'react';

import { linkable } from '../../routing';
import { colorSystem, focusedStyles, focusVisibleRing, hoveredStyles } from '../../theme';
import { StandardLayoutProps } from '../types';

export interface InteractiveCardProps extends StandardLayoutProps {
    selected?: boolean;
    selectable?: boolean;
}

function cardStyle({ selected, selectable = true, style }: InteractiveCardProps): React.CSSProperties {
    const selectedStyles = selected ? focusedStyles : {};
    const notSelectableStyles = !selectable ? { cursor: 'default', border: `1px solid ${colorSystem.neutral[2]}` } : {};
    return { ...selectedStyles, ...notSelectableStyles, ...style };
}

const InteractiveCardHost = React.forwardRef<HTMLDivElement, InteractiveCardProps>(function InteractiveCardHost(
    { selected, selectable = true, style, ...rest },
    ref,
) {
    return (
        <Container
            ref={ref}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.currentTarget.click();
                }
            }}
            tabIndex={selectable ? 0 : undefined}
            style={cardStyle({ selected, selectable, style })}
            {...rest}
        />
    );
});

/** The card on an `<a>`, without the plain card's `tabIndex` and Enter handler: the anchor has its own. */
const InteractiveCardAnchorHost = React.forwardRef<HTMLDivElement, InteractiveCardProps>(
    function InteractiveCardAnchorHost({ selected, selectable = true, style, ...rest }, ref) {
        return (
            <AnchorContainer
                ref={ref}
                {...rest}
                {...{ component: 'a' }}
                style={cardStyle({ selected, selectable, style })}
            />
        );
    },
);

/** A card to click; with `to` (and `params`, `search`, `replace`) it's a real `<a>` that takes no `onClick`. */
export const InteractiveCard = linkable(InteractiveCardHost, { linkHost: InteractiveCardAnchorHost });

// The ring's rule comes after `:focus`, so under keyboard focus it replaces the focused glow.
const Container = styled(Box)(
    {
        boxSizing: 'border-box',
        border: `1px solid ${colorSystem.neutral[2]}`,
        cursor: 'pointer',
        padding: '16px 20px',
        borderRadius: 8,
        background: colorSystem.neutral.white,
        '&:hover': {
            ...hoveredStyles,
        },
        '&:active': {
            ...focusedStyles,
        },
        '&:focus': {
            ...focusedStyles,
        },
        transition: 'background 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
    },
    focusVisibleRing,
);

/** The card as an `<a>`: a block, as the `<div>` is, without the link colour and underline. */
const AnchorContainer = styled(Container)({
    display: 'block',
    color: 'inherit',
    textDecoration: 'none',
});
